import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
	Dialog,
	DialogContent,
	DialogTitle,
	Button,
	DialogActions,
	CircularProgress,
	TextField,
	Switch,
	FormControlLabel,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Typography,
} from "@material-ui/core";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},

	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const SessionSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
});

const parseBridgeSession = raw => {
	if (!raw) return {};
	try {
		return JSON.parse(raw);
	} catch (err) {
		return {};
	}
};

const WhatsAppModal = ({
	open,
	onClose,
	whatsAppId,
	bridgeEnabled = false,
	usbOnlyMode = false,
}) => {
	const classes = useStyles();
	const initialState = {
		name: "",
		greetingMessage: "",
		farewellMessage: "",
		isDefault: false,
		counterPhone: "",
		autoDisableCounter: false,
		connectionMode: usbOnlyMode ? "usb" : bridgeEnabled ? "hybrid" : "web",
	};
	const [whatsApp, setWhatsApp] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);

	useEffect(() => {
		const fetchSession = async () => {
			if (!whatsAppId) return;

			try {
				const { data } = await api.get(`whatsapp/${whatsAppId}`);
				const bridgeData = parseBridgeSession(data.session);
				setWhatsApp({
					...data,
					connectionMode:
						data.connectionMode ||
						bridgeData.connectionMode ||
						(usbOnlyMode ? "usb" : bridgeEnabled ? "hybrid" : "web"),
				});

				const whatsQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(whatsQueueIds);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, [whatsAppId, bridgeEnabled, usbOnlyMode]);

	const handleSaveWhatsApp = async values => {
		const whatsappData = { ...values, queueIds: selectedQueueIds };

		try {
			if (whatsAppId) {
				await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
			} else {
				await api.post("/whatsapp", whatsappData);
			}
			toast.success(i18n.t("whatsappModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const handleClose = () => {
		onClose();
		setWhatsApp(initialState);
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth
				scroll="paper"
			>
				<DialogTitle>
					{whatsAppId
						? i18n.t("whatsappModal.title.edit")
						: i18n.t("whatsappModal.title.add")}
				</DialogTitle>
				<Formik
					initialValues={whatsApp}
					enableReinitialize={true}
					validationSchema={SessionSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveWhatsApp(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, touched, errors, isSubmitting }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
									<FormControlLabel
										control={
											<Field
												as={Switch}
												color="primary"
												name="isDefault"
												checked={values.isDefault}
											/>
										}
										label={i18n.t("whatsappModal.form.default")}
									/>
								</div>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.form.counterPhone")}
										name="counterPhone"
										placeholder="351966426171"
										variant="outlined"
										margin="dense"
										fullWidth
									/>
									<FormControlLabel
										control={
											<Field
												as={Switch}
												color="primary"
												name="autoDisableCounter"
												checked={values.autoDisableCounter}
											/>
										}
										label={i18n.t("whatsappModal.form.autoDisableCounter")}
									/>
								</div>
								{usbOnlyMode && (
									<div style={{ marginTop: 8, marginBottom: 8 }}>
										<Typography variant="body2" color="textSecondary">
											{i18n.t("whatsappModal.form.connectionModeUsbHint")}
										</Typography>
									</div>
								)}
								{bridgeEnabled && !usbOnlyMode && (
									<div style={{ marginTop: 8, marginBottom: 8 }}>
										<FormControl variant="outlined" margin="dense" fullWidth>
											<InputLabel id="connection-mode-label">
												{i18n.t("whatsappModal.form.connectionMode")}
											</InputLabel>
										<Field name="connectionMode">
											{({ field }) => (
												<Select
													{...field}
													labelId="connection-mode-label"
													label={i18n.t("whatsappModal.form.connectionMode")}
												>
													<MenuItem value="hybrid">
														{i18n.t("whatsappModal.form.connectionModeHybrid")}
													</MenuItem>
													<MenuItem value="web">
														{i18n.t("whatsappModal.form.connectionModeWeb")}
													</MenuItem>
												</Select>
											)}
										</Field>
										</FormControl>
										<Typography variant="caption" color="textSecondary">
											{values.connectionMode === "web"
												? i18n.t("whatsappModal.form.connectionModeWebHint")
												: i18n.t("whatsappModal.form.connectionModeHybridHint")}
										</Typography>
									</div>
								)}
								<div>
									<Field
										as={TextField}
										label={i18n.t("queueModal.form.greetingMessage")}
										type="greetingMessage"
										multiline
										rows={5}
										fullWidth
										name="greetingMessage"
										error={
											touched.greetingMessage && Boolean(errors.greetingMessage)
										}
										helperText={
											touched.greetingMessage && errors.greetingMessage
										}
										variant="outlined"
										margin="dense"
									/>
								</div>
								<div>
									<Field
										as={TextField}
										label={i18n.t("whatsappModal.form.farewellMessage")}
										type="farewellMessage"
										multiline
										rows={5}
										fullWidth
										name="farewellMessage"
										error={
											touched.farewellMessage && Boolean(errors.farewellMessage)
										}
										helperText={
											touched.farewellMessage && errors.farewellMessage
										}
										variant="outlined"
										margin="dense"
									/>
								</div>
								<QueueSelect
									selectedQueueIds={selectedQueueIds}
									onChange={selectedIds => setSelectedQueueIds(selectedIds)}
								/>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("whatsappModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{whatsAppId
										? i18n.t("whatsappModal.buttons.okEdit")
										: i18n.t("whatsappModal.buttons.okAdd")}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default React.memo(WhatsAppModal);
